import { formatCurrency } from '@/utils/function'
import dayjs from 'dayjs'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface ServiceMonthlyData {
    name: string
    total: number
    monthlyData: Record<string, number>
}

interface MonthlyTableProps {
    months: string[]
    services: ServiceMonthlyData[]
    periodLabel: string
    isComparison?: boolean
    comparisonServices?: ServiceMonthlyData[]
    comparisonMonths?: string[]
}

const MonthlyTable = ({
    months,
    services,
    periodLabel,
    isComparison = false,
    comparisonServices,
    comparisonMonths,
}: MonthlyTableProps) => {
    const { t } = useTranslation()

    const formatCurrencyWithZero = (value: number) => {
        if (value === 0) return '-'
        return formatCurrency(value)
    }

    // Extract year from period label (e.g., "01/2024 - 12/2024" -> "2024")
    const getYearLabel = () => {
        const match = periodLabel.match(
            /(\d{2})\/(\d{4})\s*-\s*(\d{2})\/(\d{4})/
        )
        if (match) {
            const startYear = match[2]
            const endYear = match[4]
            if (startYear === endYear) {
                return `Năm ${startYear}`
            }
            return `Năm ${startYear} - ${endYear}`
        }
        return periodLabel
    }

    // Get comparison value for a service at a specific month index
    const getComparisonValue = (
        serviceName: string,
        monthIndex: number
    ): number | null => {
        if (!comparisonServices || !comparisonMonths || isComparison)
            return null

        const compService = comparisonServices.find(
            (s) => s.name === serviceName
        )
        if (!compService || !comparisonMonths[monthIndex]) return null

        return compService.monthlyData[comparisonMonths[monthIndex]] || 0
    }

    // Render change indicator
    const renderChangeIndicator = (
        current: number,
        previous: number | null
    ) => {
        if (previous === null || previous === 0) return null

        const change = ((current - previous) / previous) * 100

        if (change === 0) return null

        const isPositive = change > 0

        return (
            <span
                className={`inline-flex items-center text-xs ${
                    isPositive ? 'text-green-600' : 'text-red-600'
                }`}
            >
                {isPositive ? (
                    <ArrowUp className="w-3 h-3" />
                ) : (
                    <ArrowDown className="w-3 h-3" />
                )}
            </span>
        )
    }

    return (
        <div className="space-y-2">
            <div
                className={`text-sm font-medium mb-2 ${isComparison ? 'text-gray-500' : 'text-gray-700'}`}
            >
                {getYearLabel()}
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse border border-gray-200">
                    <thead>
                        <tr className="bg-gray-50">
                            <th className="text-left py-2 px-2 font-medium text-gray-700 sticky left-0 bg-gray-50 border border-gray-200">
                                {t('employees.performance.service')}
                            </th>
                            {months.map((month) => (
                                <th
                                    key={month}
                                    className="text-right py-2 px-2 font-medium text-gray-700 whitespace-nowrap border border-gray-200"
                                >
                                    {dayjs(month).format('MM/YY')}
                                </th>
                            ))}
                            <th className="text-right py-2 px-2 font-medium text-gray-700 border border-gray-200">
                                {t('employees.performance.total')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {services.map((service, index) => (
                            <tr
                                key={service.name}
                                className={
                                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                }
                            >
                                <td
                                    className={`py-2 px-2 font-medium sticky left-0 whitespace-nowrap border border-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                >
                                    {service.name}
                                </td>
                                {months.map((month, monthIndex) => {
                                    const value =
                                        service.monthlyData[month] || 0
                                    const compValue = getComparisonValue(
                                        service.name,
                                        monthIndex
                                    )
                                    return (
                                        <td
                                            key={month}
                                            className="text-right py-2 px-2 whitespace-nowrap border border-gray-200"
                                        >
                                            {renderChangeIndicator(
                                                value,
                                                compValue
                                            )}
                                            <span
                                                className={
                                                    value === 0
                                                        ? 'text-gray-400'
                                                        : ''
                                                }
                                            >
                                                {formatCurrencyWithZero(value)}
                                            </span>
                                        </td>
                                    )
                                })}
                                <td className="text-right py-2 px-2 font-medium whitespace-nowrap border border-gray-200">
                                    {(() => {
                                        const compService =
                                            comparisonServices?.find(
                                                (s) => s.name === service.name
                                            )
                                        return (
                                            <>
                                                {renderChangeIndicator(
                                                    service.total,
                                                    compService?.total || null
                                                )}
                                                {formatCurrencyWithZero(
                                                    service.total
                                                )}
                                            </>
                                        )
                                    })()}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default MonthlyTable
